import type { MedplumClient } from '@medplum/core';
import { UserFacingError } from './userFacingError';

/**
 * Load PDF bytes for historical DocumentReference attachments.
 *
 * Callers must already have resolved bytes (typically via `download-document-attachment`
 * into a `data:` URL). Direct `Binary/{id}` downloads are denied because staff policies
 * have no Binary grant and Medplum does not enforce Binary.securityContext.
 */
export async function fetchHistoricalPdfBlob(
  _medplum: MedplumClient,
  attachmentUrl: string,
): Promise<Blob> {
  if (attachmentUrl.startsWith('data:') || attachmentUrl.startsWith('blob:')) {
    return (await fetch(attachmentUrl)).blob();
  }
  throw new Error('Historical PDFs must be opened through the document attachment proxy.');
}

/**
 * Open an historical PDF in a new browser tab (blob URL).
 *
 * Opens a blank tab **synchronously** on the user gesture, then navigates it after
 * the bytes load — otherwise browsers treat `window.open` after `await` as a blocked
 * popup (common on localhost).
 */
export async function openHistoricalPdfInNewTab(
  medplum: MedplumClient,
  attachmentUrl: string,
): Promise<void> {
  const win = window.open('about:blank', '_blank');
  if (!win) {
    throw new UserFacingError('Popup blocked — allow pop-ups to view the PDF in a new tab.');
  }

  try {
    const blob = await fetchHistoricalPdfBlob(medplum, attachmentUrl);
    const objectUrl = URL.createObjectURL(blob);
    win.location.href = objectUrl;
    globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (err) {
    win.close();
    throw err;
  }
}
